import { formatDateDisplay } from "../hooks/useDataManagerOrdersUtils.js";
import {
  buildOrderAdvancedFilterChips,
  isOrderSearchQueryActive,
  resolveActiveOrderSearchQuery,
} from "./dataManagerOrderSearch.js";
import {
  ORDER_PDF_DEFAULT_COLUMN_KEYS,
  ORDER_PDF_COLUMN_CATALOG_BY_KEY,
  buildOrderPdfExportColumns,
  getOrderReportFieldDisplayValue,
} from "./dataManagerOrdersCustomization.js";

export const ORDER_PDF_EXPORT_COLUMNS = Object.freeze(
  ORDER_PDF_DEFAULT_COLUMN_KEYS.map((key) => ({
    key,
    label: ORDER_PDF_COLUMN_CATALOG_BY_KEY[key]?.label || key,
  })),
);

export function buildDataManagerOrdersExportViewModel({
  activeFilters = [],
  exportColumns = null,
  exportedAt = new Date().toISOString(),
  rows = [],
  scopeLabel = "",
  searchQuery = "",
  sortState = { direction: "asc", key: null },
  title = "Commandes",
} = {}) {
  const resolvedExportColumns = Array.isArray(exportColumns) && exportColumns.length > 0
    ? exportColumns
    : buildOrderPdfExportColumns();

  return {
    activeFilters: Array.isArray(activeFilters) ? activeFilters : [],
    columns: resolvedExportColumns.map((column) => ({
      key: column.key,
      label: column.label,
      widthHint: Number(column.widthHint) || null,
    })),
    exportedAt,
    resultCount: Array.isArray(rows) ? rows.length : 0,
    rows: (Array.isArray(rows) ? rows : []).map((order) => Object.fromEntries(
      resolvedExportColumns.map((column) => [column.key, getOrderReportFieldDisplayValue(order, column.key)]),
    )),
    scopeLabel,
    searchQuery: resolveActiveOrderSearchQuery(searchQuery),
    sort: {
      column: sortState?.key || null,
      direction: sortState?.direction || "asc",
    },
    title,
  };
}

export function buildOrdersScopeLabel({
  currentDispatchDates = [],
  isBrowseMode = false,
  orderBrowseDateFilter = "",
} = {}) {
  if (isBrowseMode && orderBrowseDateFilter) {
    return `Date ${formatDateDisplay(orderBrowseDateFilter)}`;
  }

  if (isBrowseMode) {
    return "Toutes les commandes";
  }

  if (Array.isArray(currentDispatchDates) && currentDispatchDates.length > 0) {
    return `Dispatch ${currentDispatchDates.map((value) => formatDateDisplay(value)).join(" · ")}`;
  }

  return "Dispatch courant";
}

export function shouldResyncOrdersReportDateRange({
  previousDispatchDate = "",
  dateFrom = "",
  dateTo = "",
} = {}) {
  const previous = typeof previousDispatchDate === "string" ? previousDispatchDate.trim() : "";
  const from = typeof dateFrom === "string" ? dateFrom.trim() : "";
  const to = typeof dateTo === "string" ? dateTo.trim() : "";
  return from === previous && to === previous;
}

export function buildOrdersPeriodScopeLabel({ dateFrom = "", dateTo = "" } = {}) {
  const from = typeof dateFrom === "string" ? dateFrom.trim() : "";
  const to = typeof dateTo === "string" ? dateTo.trim() : "";
  if (!from && !to) {
    return "";
  }

  if (from && to && from !== to) {
    return `Période ${formatDateDisplay(from)} à ${formatDateDisplay(to)}`;
  }

  return `Date ${formatDateDisplay(from || to)}`;
}

export function buildOrdersPeriodActiveFilters({
  clientName = "",
  dateFrom = "",
  dateTo = "",
} = {}) {
  const filters = [];
  const from = typeof dateFrom === "string" ? dateFrom.trim() : "";
  const to = typeof dateTo === "string" ? dateTo.trim() : "";
  const scopeLabel = buildOrdersPeriodScopeLabel({ dateFrom: from, dateTo: to });
  if (scopeLabel) {
    const isRange = Boolean(from && to && from !== to);
    filters.push({
      key: isRange ? "period" : "date",
      label: isRange ? "Période" : "Date",
      value: scopeLabel.replace(/^(Période|Date)\s+/, ""),
    });
  }

  const resolvedClientName = typeof clientName === "string" ? clientName.trim() : "";
  if (resolvedClientName) {
    filters.push({
      key: "client_name",
      label: "Client livré",
      value: resolvedClientName,
    });
  }

  return filters;
}

export function buildOrdersActiveFilters({
  advancedFilters = {},
  isBrowseMode = false,
  isPmOnlyFilter = false,
  orderBrowseDateFilter = "",
  searchQuery = "",
} = {}) {
  const filters = [];

  if (isOrderSearchQueryActive(searchQuery)) {
    filters.push({ key: "search", label: "Recherche", value: resolveActiveOrderSearchQuery(searchQuery) });
  }

  if (orderBrowseDateFilter) {
    filters.push({ key: "date", label: "Date", value: formatDateDisplay(orderBrowseDateFilter) });
  } else if (isBrowseMode) {
    filters.push({ key: "scope", label: "Scope", value: "Toutes les commandes" });
  }

  if (isPmOnlyFilter) {
    filters.push({ key: "pm", label: "Filtre", value: "Chargé PM" });
  }

  filters.push(...buildOrderAdvancedFilterChips(advancedFilters));

  return filters;
}
